import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import { Box, Chip, chipClasses, FormHelperText, OutlinedInput } from '@mui/material';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Controller, RegisterOptions, useFormContext } from 'react-hook-form';
import { Minimum_Order_Value_Type_Enum } from '@kheops/graphql';
import PackageIcon from '../../assets/icons/package.svg?react';
import WeightIcon from '../../assets/icons/weight.svg?react';

type MinimumOrderValueTypeInfo = {
  label: string,
  icon: React.ReactElement,
  endAdornment: string;
  validate?: RegisterOptions['validate'];
}

export const MinimumOrderValueTypeInfoMap: {[key in Minimum_Order_Value_Type_Enum]: MinimumOrderValueTypeInfo} = {
  [Minimum_Order_Value_Type_Enum.Price]: {
    label: 'common:minimum_order_value_price_type',
    icon: <PaymentsOutlinedIcon />,
    endAdornment: '€',
    validate: {
      validFormat: (value) => Number.isInteger(parseFloat(value)) || 'validation:this_value_should_be_a_integer',
    },
  },
  [Minimum_Order_Value_Type_Enum.Weight]: {
    label: 'common:minimum_order_value_weight_type',
    icon: <WeightIcon />,
    endAdornment: 'kg',
  },
  [Minimum_Order_Value_Type_Enum.Packaging]: {
    label: 'common:minimum_order_value_packaging_type',
    icon: <PackageIcon />,
    endAdornment: 'packaging:case',
    validate: {
      validFormat: (value) => Number.isInteger(parseFloat(value)) || 'validation:this_value_should_be_a_integer',
    },
  },
};

export interface MinimumOrderValueFieldProps {
  minimumOrderValueName: string;
  minimumOrderValueTypeName: string;
  hideType?: boolean;
}

export default function MinimumOrderValueField({ minimumOrderValueName, minimumOrderValueTypeName, hideType }: MinimumOrderValueFieldProps): React.JSX.Element {
  const { t } = useTranslation(['common', 'packaging', 'validation']);
  const { control, watch, setValue, trigger, formState: { isDirty } } = useFormContext();
  const currentType: Minimum_Order_Value_Type_Enum = watch(minimumOrderValueTypeName);

  useEffect((): void => {
    if (isDirty) {
      trigger(minimumOrderValueName);
    }
  }, [currentType, isDirty]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Box sx={{ display: hideType ? 'none' : 'flex', flexWrap: 'wrap', gap: 1 }}>
        {(Object.keys(MinimumOrderValueTypeInfoMap) as Minimum_Order_Value_Type_Enum[]).map((key) => (
          <Chip
            key={key}
            className={key === currentType ? 'Kheops-selected' : ''}
            variant="filter"
            onClick={() => setValue(minimumOrderValueTypeName, key, { shouldDirty: true })}
            label={t(MinimumOrderValueTypeInfoMap[key].label)}
            icon={MinimumOrderValueTypeInfoMap[key].icon}
            sx={{
              [`& .${chipClasses.icon}`]: {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                fill: (theme) => (theme.palette as any).primary.main,
              },
            }}
          />
        ))}
      </Box>
      <Controller
        name={minimumOrderValueName}
        control={control}
        rules={{
          required: t('validation:this_field_is_required') as string,
          min: { value: 0, message: t('validation:this_value_should_be_higher_than_or_equals_to_N', { value: 0 }) },
          validate: MinimumOrderValueTypeInfoMap[currentType].validate,
        }}
        render={({ field, fieldState }) => (
          <>
            <OutlinedInput
              {...field}
              onChange={(e) => field.onChange(e.target.value === '' ? '' : parseFloat(e.target.value))}
              fullWidth
              type="number"
              inputProps={{
                min: 0,
                step: currentType === Minimum_Order_Value_Type_Enum.Weight ? 0.01 : 1,
              }}
              sx={{
                '& svg': {
                  height: 20,
                  width: 20,
                },
              }}
              startAdornment={MinimumOrderValueTypeInfoMap[currentType].icon}
              endAdornment={t(MinimumOrderValueTypeInfoMap[currentType].endAdornment)}
              placeholder={t('common:your_minimum_order_value')}
            />
            {!!fieldState.error && (
              <FormHelperText sx={{ ml: 2 }} error>{t(fieldState.error.message!)}</FormHelperText>
            )}
          </>
        )}
      />
    </Box>
  );
}
